import { createRouter, createWebHistory } from "vue-router";
import MainLayout from "../layouts/MainLayout.vue";
import i18n from "../../i18n";
import { initMetrika, trackPageView } from "../common/helpers/metrika";
import { resolveAuthNavigation } from "./authGuard";
import { resolveBillingNavigation } from "./billingGuard";

const pages = import.meta.glob("../**/*.vue");

function loadComponent(path: string) {
  const importer = pages[`${path}.vue`];
  if (!importer) {
    throw new Error(`Component not found: ${path}.vue`);
  }
  return importer;
}

const t = (key: string) => i18n.global.t(key);

const isMpEnabled = import.meta.env.VITE_MP_ENABLED === "true";

const disabledPrefixes = [
  "/app/zakooper",
  ...(isMpEnabled ? [] : ["/app/marketplace"]),
  "/app/pricekeeper",
  "/app/bi",
  "/app/settings",
];

const routes = [
  {
    path: "/",
    meta: { requiresAuth: true },
    redirect: "/app/eplex/single-search",
  },
  {
    path: "/login",
    name: "login",
    meta: { requiresAuth: false, titleKey: "router.loginTitle" },
    props: { resetLink: "/reset-password", createAccountLink: "/create-account" },
    component: loadComponent("../components/LoginPage"),
  },
  {
    path: "/registration",
    name: "registration",
    meta: { requiresAuth: false, titleKey: "router.registrationTitle" },
    component: loadComponent("../components/RegistrationForm"),
  },
  {
    path: "/billing/return",
    name: "billing-return",
    meta: { requiresAuth: false, titleKey: "router.billingReturnTitle" },
    component: loadComponent("../modules/Eplex/pages/EplexBillingReturn"),
  },
  {
    path: "/app",
    meta: { requiresAuth: true },
    component: MainLayout,
    children: [
      { path: "", redirect: "eplex" },
      {
        path: "eplex",
        children: [
          { path: "", redirect: "journal" },
          {
            path: "journal",
            component: loadComponent("../modules/Eplex/pages/EplexJournal"),
            meta: { titleKey: "router.journalTitle" },
          },
          {
            path: "single-search",
            component: loadComponent("../modules/Eplex/pages/EplexMultiSearch"),
            meta: { titleKey: "router.singleSearchTitle" },
          },
          {
            path: "retail-pricing",
            component: loadComponent("../modules/Eplex/pages/EplexRetailPricing"),
            meta: { titleKey: "router.retailPricingTitle" },
          },
          {
            path: "selection",
            component: loadComponent("../modules/Eplex/pages/EplexSelection"),
            meta: { titleKey: "router.selectionTitle" },
          },
          {
            path: "selection/new",
            name: "eplex-selection-new",
            component: loadComponent("../modules/Eplex/pages/EplexSelection"),
            meta: { titleKey: "router.selectionTitle" },
            props: { mode: "create" },
          },
          {
            path: "selection/:selectionId",
            name: "eplex-selection-detail",
            component: loadComponent("../modules/Eplex/pages/EplexSelection"),
            meta: { titleKey: "router.selectionTitle" },
            props: true,
          },
          {
            path: "function-settings",
            component: loadComponent("../modules/Eplex/pages/EplexFunctionSettings"),
            meta: { titleKey: "router.pluginsSettingsTitle" },
          },
          {
            path: "event-feed",
            component: loadComponent("../modules/Eplex/pages/EplexJournalTimelineGrid"),
            meta: { titleKey: "router.eventFeedTitle" },
          },
          {
            path: "markup-settings",
            component: loadComponent("../modules/Eplex/pages/EplexMarkupSettings"),
            meta: { titleKey: "router.markupSettingsTitle" },
          },
          {
            path: "integration-1c",
            component: loadComponent("../modules/Eplex/pages/EplexIntegrationSettings"),
            meta: { titleKey: "router.integration1cTitle" },
          },
          {
            path: "license-settings",
            component: loadComponent("../modules/Eplex/pages/EplexLicense"),
            meta: { titleKey: "router.licenseTitle" },
          },
          {
            path: "personalization",
            component: loadComponent("../modules/Eplex/pages/EplexPersonalization"),
            meta: { titleKey: "router.personalizationTitle" },
          },
          {
            path: "billing",
            component: loadComponent("../modules/Eplex/pages/EplexBilling"),
            meta: { titleKey: "router.billingTitle" },
          },
          {
            path: "guidance",
            component: loadComponent("../modules/Eplex/pages/EplexGuidance"),
            meta: { titleKey: "router.guidanceTitle" },
          },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
              {
                path: "about-company",
                component: loadComponent("../modules/Support/pages/SupportAboutCompany"),
                meta: { titleKey: "router.aboutCompanyTitle" },
              },
            ],
          },
        ],
      },
      {
        path: "marketplace",
        children: [
          { path: "", redirect: "summary" },
          {
            path: "summary",
            component: loadComponent("../modules/Marketplace/pages/MarketplaceSummary"),
            meta: { titleKey: "router.marketplaceSummaryTitle" },
          },
          {
            path: "summary/processes",
            component: loadComponent("../modules/Marketplace/components/summary/MarketplaceSummaryProcessesDetails"),
            meta: { titleKey: "router.marketplaceSummaryProcessesTitle" },
          },
          {
            path: "summary/sales-details-profit",
            component: loadComponent("../modules/Marketplace/components/summary/MarketplaceSummarySalesDetailsProfit"),
            meta: { titleKey: "router.marketplaceSummarySalesProfitTitle" },
          },
          {
            path: "summary/sales-details-orders",
            component: loadComponent("../modules/Marketplace/components/summary/MarketplaceSummarySalesDetailsOrders"),
            meta: { titleKey: "router.marketplaceSummarySalesOrdersTitle" },
          },
          {
            path: "summary/price-lists",
            component: loadComponent("../modules/Marketplace/components/summary/MarketplaceSummaryPriceListsTables"),
            meta: { titleKey: "router.marketplaceSummaryPriceListsTitle" },
          },
          {
            path: "pivot-summary",
            meta: { titleKey: "router.marketplacePivotSummaryTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplacePivotSummary"),
          },
          {
            path: "prices",
            meta: { titleKey: "router.marketplaceMyPriceTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceMyPrices"),
          },
          {
            path: "prices/:uuid",
            name: "marketplace-my-price",
            meta: { titleKey: "router.marketplaceMyPriceTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceMyPrice"),
            props: true,
          },
          {
            path: "hypothesis",
            meta: { titleKey: "router.marketplaceHypothesisTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceHypothesis"),
          },
          { path: "orders", meta: { titleKey: "router.marketplaceOrdersTitle" }, redirect: "/app/marketplace/orders/new" },
          {
            path: "orders/:tab/:uuid?/:footerTab?",
            meta: { titleKey: "router.marketplaceOrdersTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceOrders"),
            props: true,
          },
          {
            path: "returns",
            meta: { titleKey: "router.marketplaceReturnsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceReturns"),
          },
          {
            path: "reviews",
            meta: { titleKey: "router.marketplaceReviewsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceReviews"),
          },
          {
            path: "event-feed",
            meta: { titleKey: "router.marketplaceEventFeedTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceEventFeed"),
          },
          {
            path: "reports",
            meta: { titleKey: "router.marketplaceReportsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceReports"),
          },
          {
            path: "my-price-settings",
            meta: { titleKey: "router.marketplaceMyPriceSettingsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceMyPriceSettings"),
          },
          {
            path: "automation-order-processes",
            meta: { titleKey: "router.marketplaceAutomationTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceAutomationOrderProcesses"),
          },
          {
            path: "deviations-settings",
            meta: { titleKey: "router.marketplaceDeviationsSettingsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceDeviationSettings"),
          },
          {
            path: "platforms-settings",
            meta: { titleKey: "router.marketplacePlatformsSettingsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplacePlatformsSettings"),
          },
          {
            path: "plugins-settings",
            meta: { titleKey: "router.marketplacePluginsSettingsTitle" },
            component: loadComponent("../modules/Eplex/pages/EplexFunctionSettings"),
          },
          {
            path: "zakooper-settings",
            meta: { titleKey: "router.marketplaceZakooperSettingsTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceZakooperSettings"),
          },
          {
            path: "email-account",
            meta: { titleKey: "router.marketplaceEmailAccountTitle" },
            component: loadComponent("../modules/Marketplace/pages/MarketplaceEmailAccount"),
          },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
            ],
          },
        ],
      },
      {
        path: "zakooper",
        children: [
          { path: "", redirect: "zakooper" },
          { path: "zakooper", component: loadComponent("../modules/Zakooper/pages/Zakooper") },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
            ],
          },
        ],
      },
      {
        path: "bi",
        children: [
          { path: "", redirect: "dashboard" },
          { path: "dashboard", component: loadComponent("../modules/BI/pages/BIDashboard") },
          { path: "analytics", component: loadComponent("../modules/BI/pages/BIAnalytics") },
          { path: "supplier-potential", component: loadComponent("../modules/BI/pages/BISupplierPotential") },
          { path: "overpayments", component: loadComponent("../modules/BI/pages/BIOverpayments") },
          { path: "sales-potential", component: loadComponent("../modules/BI/pages/BISalesPotential") },
          { path: "supplier-table", component: loadComponent("../modules/BI/pages/BISupplierTable") },
          { path: "overpayment-table", component: loadComponent("../modules/BI/pages/BIOverpaymentTable") },
          { path: "sales-potential-table", component: loadComponent("../modules/BI/pages/BISalesPotentialTable") },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
            ],
          },
        ],
      },
      {
        path: "pricekeeper",
        children: [
          { path: "", redirect: "pricekeeper" },
          { path: "pricekeeper", component: loadComponent("../modules/Pricekeeper/pages/Pricekeeper") },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
            ],
          },
        ],
      },
      {
        path: "settings",
        children: [
          { path: "", redirect: "settings" },
          { path: "settings", component: loadComponent("../modules/Settings/pages/Settings") },
          {
            path: "support",
            children: [
              { path: "", redirect: "contact" },
              {
                path: "contact",
                component: loadComponent("../modules/Support/pages/SupportContact"),
                meta: { titleKey: "router.supportTitle" },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.afterEach((to) => {
  const defaultTitle = t("router.defaultTitle");
  const titleKey = to.meta?.titleKey as string | undefined;
  document.title = titleKey ? t(titleKey) : defaultTitle;
});

// Яндекс.Метрика: счётчики подключаются на первой навигации (только на проде), и каждый
// переход между разделами уходит виртуальным просмотром — SPA не перезагружает страницу,
// и без этого Метрика видела бы один просмотр на всю сессию
router.afterEach(() => {
  initMetrika();
  trackPageView();
});

router.beforeEach((to, _from, next) => {
  if (disabledPrefixes.some((prefix) => to.path.startsWith(prefix))) {
    alert(t("router.moduleBlocked"));
    next(false);
    return;
  }

  resolveAuthNavigation(to, next);
});

// После проверки сессии: гард биллинга ходит в API, а без сессии тот ответил бы 401
router.beforeEach((to) => resolveBillingNavigation(to));

export default router;
